import { useCallback, useEffect, useState } from 'react'
import {
  arrangeFrames,
  boardTemplateBounds,
  BOARD_TEMPLATE_FRAME_H,
  BOARD_TEMPLATE_FRAME_W,
  BOARD_TEMPLATE_MAX_FRAMES,
  normalizeBoardTemplate,
  TEMPLATE_SCOPE_HINT,
  TEMPLATE_SCOPE_LABEL,
  type BoardTemplateContent,
  type BoardTemplateDto,
  type BoardTemplateFrame,
  type Frame,
  type MeDto,
  type TemplateScope,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'

/**
 * Boardvorlagen — Gerüste aus leeren Frames.
 *
 * Bewusst getrennt von den Frame-Vorlagen, obwohl beide „Vorlage" heißen: Die
 * eine bringt einen fertigen Frame samt Inhalt und Dateien, die andere nur die
 * Bühnen, auf denen später etwas steht. In der Oberfläche steht deshalb nirgends
 * bloß „Vorlage", sondern immer „Frame-Vorlage" oder „Boardvorlage".
 */

/** Lädt die Gerüste einer Organisation und hält sie beim Löschen aktuell. */
export function useBoardTemplates(orgId: string | null): {
  templates: BoardTemplateDto[] | null
  error: string | null
  add: (template: BoardTemplateDto) => void
  remove: (id: string) => Promise<void>
} {
  const t = useText()
  const [templates, setTemplates] = useState<BoardTemplateDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    api
      .boardTemplates(orgId)
      .then((loaded) => {
        if (!cancelled) setTemplates(loaded)
      })
      .catch(() => {
        if (!cancelled) setError(t('boardTemplates.loadFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [orgId, t])

  const add = useCallback((template: BoardTemplateDto) => {
    setTemplates((current) => [...(current ?? []), template])
  }, [])

  const remove = useCallback(async (id: string) => {
    await api.deleteBoardTemplate(id)
    setTemplates((current) => (current ?? []).filter((template) => template.id !== id))
  }, [])

  return { templates, error, add, remove }
}

/**
 * Die Kacheln, gruppiert nach Reichweite.
 *
 * Dieselbe Darstellung im Anlege-Fenster und im Board: Wer das Gerüst einmal
 * ausgesucht hat, erkennt es an derselben Stelle wieder. `selectedId` macht aus
 * der Liste eine Auswahl — ohne sie ist jede Kachel ein Knopf, der sofort tut.
 */
export function BoardTemplateTiles({
  templates,
  selectedId,
  busyId,
  onPick,
  onRemove,
}: {
  templates: BoardTemplateDto[]
  selectedId?: string | null
  busyId?: string | null
  onPick: (template: BoardTemplateDto) => void
  onRemove?: (template: BoardTemplateDto) => void
}): React.ReactElement {
  const t = useText()
  const groups: { scope: TemplateScope; items: BoardTemplateDto[] }[] = (
    ['platform', 'organisation', 'personal'] as const
  )
    .map((scope) => ({ scope, items: templates.filter((template) => template.scope === scope) }))
    .filter((group) => group.items.length > 0)

  return (
    <>
      {groups.map((group) => (
        <section key={group.scope}>
          <h4 className="dialog-section">{t(TEMPLATE_SCOPE_LABEL[group.scope])}</h4>
          <ul className="template-grid">
            {group.items.map((template) => (
              <li key={template.id}>
                <BoardTemplateCard
                  template={template}
                  selected={selectedId === undefined ? undefined : selectedId === template.id}
                  disabled={busyId != null}
                  onPick={onPick}
                />
                {onRemove && template.canManage && !template.builtin && (
                  <button
                    type="button"
                    className="button ghost icon template-remove"
                    onClick={() => onRemove(template)}
                    aria-label={t('boardTemplates.deleteLabel', { name: template.name })}
                    title={t('boardTemplates.delete')}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

/**
 * Eine Kachel.
 *
 * Eigenständig, weil sie in zwei Anordnungen vorkommt: gruppiert im
 * Board-Fenster und als eine Reihe im Anlege-Fenster, wo die Überschriften der
 * Reichweiten den Dialog über den Bildrand schöben.
 */
export function BoardTemplateCard({
  template,
  selected,
  disabled,
  onPick,
}: {
  template: BoardTemplateDto
  selected?: boolean
  disabled?: boolean
  onPick: (template: BoardTemplateDto) => void
}): React.ReactElement {
  const t = useText()

  return (
    <button
      type="button"
      className={`template-card${selected ? ' is-selected' : ''}`}
      onClick={() => onPick(template)}
      disabled={disabled}
      aria-pressed={selected}
      title={
        template.builtin
          ? undefined
          : `${t(TEMPLATE_SCOPE_LABEL[template.scope])} · ${template.authorName}`
      }
    >
      <BoardTemplateSketch content={template.content} />
      <span className="template-name">{template.name}</span>
      <span className="template-meta muted small">
        {t.plural(template.content.frames.length, 'boardTemplates.frame.one', 'boardTemplates.frame.many')}
        {!template.builtin && ` · ${template.authorName}`}
      </span>
    </button>
  )
}

/**
 * Gerüst-Auswahl im Board.
 *
 * Hier wird sofort eingesetzt — anders als im Anlege-Fenster, wo die Auswahl
 * erst mit dem Board entsteht. Darunter die beiden Wege zu einem eigenen
 * Gerüst: aus diesem Board ziehen oder von Hand zusammenstellen.
 */
export function BoardTemplatesDialog({
  orgId,
  me,
  boardFrames,
  notes,
  onApply,
  onClose,
}: {
  orgId: string
  me: MeDto
  /** Die Frames dieses Boards — Grundlage für „Aus diesem Board". */
  boardFrames: readonly Frame[]
  /** Sprechernotiz je Frame; fehlt, wenn niemand sie lesen darf. */
  notes?: (frameId: string) => string
  onApply: (template: BoardTemplateDto) => Promise<void>
  onClose: () => void
}): React.ReactElement {
  const { templates, error: loadError, add, remove } = useBoardTemplates(orgId)
  const t = useText()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [composing, setComposing] = useState(false)

  async function use(template: BoardTemplateDto): Promise<void> {
    setError(null)
    setBusyId(template.id)
    try {
      await onApply(template)
      onClose()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('boardTemplates.applyFailed'))
      setBusyId(null)
    }
  }

  if (saving) {
    return (
      <SaveBoardTemplateDialog
        orgId={orgId}
        me={me}
        frames={boardFrames}
        notes={notes}
        onClose={() => setSaving(false)}
        onSaved={(template) => {
          add(template)
          setSaving(false)
        }}
      />
    )
  }

  if (composing) {
    return (
      <ComposeBoardTemplateDialog
        orgId={orgId}
        me={me}
        onClose={() => setComposing(false)}
        onSaved={(template) => {
          add(template)
          setComposing(false)
        }}
      />
    )
  }

  return (
    <Dialog title={t('boardTemplates.applyTitle')} onClose={onClose}>
      <p className="muted small">{t('boardTemplates.applyLead')}</p>

      {templates === null && !loadError && <p className="muted small">{t('common.loading')}</p>}
      {templates && (
        <BoardTemplateTiles templates={templates} busyId={busyId} onPick={(t) => void use(t)} onRemove={(t) => void remove(t.id)} />
      )}

      <div className="dialog-actions">
        <button
          type="button"
          className="button ghost"
          onClick={() => setSaving(true)}
          disabled={boardFrames.length === 0}
          title={
            boardFrames.length === 0
              ? t('boardTemplates.noFrames')
              : t('boardTemplates.fromBoardTitle')
          }
        >
          {t('boardTemplates.fromBoard')}
        </button>
        <button type="button" className="button ghost" onClick={() => setComposing(true)}>
          {t('boardTemplates.compose')}
        </button>
      </div>

      {busyId && <p className="muted small">{t('boardTemplates.applying')}</p>}
      {(error ?? loadError) && <p className="form-error">{error ?? loadError}</p>}
    </Dialog>
  )
}

/** Die Frames eines Boards als Gerüst sichern. */
export function SaveBoardTemplateDialog({
  orgId,
  me,
  frames,
  notes,
  onClose,
  onSaved,
}: {
  orgId: string
  me: MeDto
  frames: readonly Frame[]
  /** Sprechernotiz je Frame; fehlt, wenn niemand sie lesen darf. */
  notes?: (frameId: string) => string
  onClose: () => void
  onSaved: (template: BoardTemplateDto) => void
}): React.ReactElement {
  const t = useText()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<TemplateScope>('personal')
  // Alles vorbelegt an: Wer ein Gerüst aus einem fertigen Board zieht, will in
  // aller Regel das ganze Gerüst — abwählen kann es, wer weniger will.
  const [withNames, setWithNames] = useState(true)
  const [withStyle, setWithStyle] = useState(true)
  const [withNotes, setWithNotes] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const noted = notes ? frames.filter((frame) => notes(frame.id).trim() !== '').length : 0

  const content: BoardTemplateContent = {
    frames: normalizeBoardTemplate(
      [...frames]
        .sort((a, b) => a.order - b.order)
        .slice(0, BOARD_TEMPLATE_MAX_FRAMES)
        .map((frame, index) => ({
          name: withNames ? frame.name : `Frame ${index + 1}`,
          x: frame.x,
          y: frame.y,
          w: frame.w,
          h: frame.h,
          style: withStyle
            ? {
                background: frame.background,
                borderColor: frame.borderColor,
                borderWidth: frame.borderWidth,
                padding: frame.padding,
                columns: frame.columns,
                rows: frame.rows,
              }
            : null,
          notes: withNotes && notes ? notes(frame.id) : '',
        })),
    ),
  }

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      onSaved(await api.createBoardTemplate({ orgId, name: name.trim(), scope, content }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('boardTemplates.saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('boardTemplates.saveTitle')} onClose={onClose}>
      <p className="muted small">{t('boardTemplates.saveLead')}</p>

      <form onSubmit={save}>
        <label className="field">
          <span>{t('common.name')}</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('boardTemplates.namePlaceholder')}
            required
            autoFocus
            maxLength={120}
          />
        </label>

        <div className="field">
          <span>{t('boardTemplates.included')}</span>
          <label className="check">
            <input
              type="checkbox"
              checked={withNames}
              onChange={(event) => setWithNames(event.target.checked)}
            />
            <span>
              {t('boardTemplates.withNames')}
              <small className="muted">{t('boardTemplates.withNamesHint')}</small>
            </span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={withStyle}
              onChange={(event) => setWithStyle(event.target.checked)}
            />
            <span>
              {t('boardTemplates.withStyle')}
              <small className="muted">{t('boardTemplates.withStyleHint')}</small>
            </span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={withNotes}
              onChange={(event) => setWithNotes(event.target.checked)}
              disabled={!notes || noted === 0}
            />
            <span>
              {t('boardTemplates.withNotes')}
              <small className="muted">
                {notes
                  ? noted === 0
                    ? t('boardTemplates.notesNone')
                    : t('boardTemplates.notesSome', { noted, total: frames.length })
                  : t('boardTemplates.notesUnavailable')}
              </small>
            </span>
          </label>
        </div>

        <ScopeChoice scope={scope} onChange={setScope} me={me} />

        <BoardTemplateSketch content={content} />
        <p className="muted small">
          {t('boardTemplates.inSkeleton', {
            frames: t.plural(
              content.frames.length,
              'boardTemplates.frame.one',
              'boardTemplates.frame.many',
            ),
          })}
        </p>

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('templates.saving') : t('boardTemplates.save')}
        </button>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Dialog>
  )
}

/** Seitenverhältnisse, die zur Auswahl stehen — dieselben wie bei Beamern und Bildschirmen. */
const FORMATS: { label: string; ratio: number }[] = [
  { label: '16:10', ratio: 16 / 10 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '4:3', ratio: 4 / 3 },
  { label: '1:1', ratio: 1 },
]

/**
 * Ein Gerüst von Hand zusammenstellen — ohne Board.
 *
 * Der zweite Weg neben „aus diesem Board": Wer weiß, wie sein Ablauf aussieht,
 * soll ihn hinschreiben können, ohne ihn vorher einmal gebaut zu haben.
 */
export function ComposeBoardTemplateDialog({
  orgId,
  me,
  onClose,
  onSaved,
}: {
  orgId: string
  me: MeDto
  onClose: () => void
  onSaved: (template: BoardTemplateDto) => void
}): React.ReactElement {
  const t = useText()
  const [name, setName] = useState('')
  const [scope, setScope] = useState<TemplateScope>('personal')
  const [names, setNames] = useState<string[]>(() => [
    t('bt.talk.title'),
    t('bt.talk.agenda'),
    t('bt.talk.topic1'),
    t('bt.talk.conclusion'),
  ])
  const [columns, setColumns] = useState(0)
  const [format, setFormat] = useState(FORMATS[0]!.ratio)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const height = BOARD_TEMPLATE_FRAME_H
  const width = Math.round(height * format)
  const content: BoardTemplateContent = {
    frames: arrangeFrames(names, { columns, width, height }),
  }

  function setCount(next: number): void {
    const count = Math.max(1, Math.min(BOARD_TEMPLATE_MAX_FRAMES, next))
    setNames((current) =>
      count <= current.length
        ? current.slice(0, count)
        : [...current, ...Array.from({ length: count - current.length }, () => '')],
    )
  }

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      onSaved(await api.createBoardTemplate({ orgId, name: name.trim(), scope, content }))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('boardTemplates.saveFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('boardTemplates.composeTitle')} onClose={onClose}>
      <form onSubmit={save}>
        <label className="field">
          <span>{t('common.name')}</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('boardTemplates.composePlaceholder')}
            required
            autoFocus
            maxLength={120}
          />
        </label>

        <div className="field">
          <span>{t('boardTemplates.layout')}</span>
          <span className="inline-row">
            <label className="inline-field">
              <span className="muted small">{t('boardTemplates.frames')}</span>
              <input
                type="number"
                min={1}
                max={BOARD_TEMPLATE_MAX_FRAMES}
                value={names.length}
                onChange={(event) => setCount(Number(event.target.value))}
              />
            </label>
            <label className="inline-field">
              <span className="muted small">{t('boardTemplates.columns')}</span>
              <select value={columns} onChange={(event) => setColumns(Number(event.target.value))}>
                <option value={0}>{t('boardTemplates.oneRow')}</option>
                {[2, 3, 4, 5, 6].map((value) => (
                  <option key={value} value={value}>
                    {t('frame.columns', { count: value })}
                  </option>
                ))}
              </select>
            </label>
            <label className="inline-field">
              <span className="muted small">{t('boardTemplates.format')}</span>
              <select value={format} onChange={(event) => setFormat(Number(event.target.value))}>
                {FORMATS.map((entry) => (
                  <option key={entry.label} value={entry.ratio}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </label>
          </span>
        </div>

        <BoardTemplateSketch content={content} />

        <div className="field">
          <span>{t('boardTemplates.labels')}</span>
          <ol className="frame-name-list">
            {names.map((entry, index) => (
              // Der Index als Schlüssel ist hier richtig: Die Zeilen haben keine
              // eigene Identität, sie sind schlicht Platz eins bis n.
              <li key={index}>
                <input
                  value={entry}
                  onChange={(event) =>
                    setNames((current) =>
                      current.map((value, position) =>
                        position === index ? event.target.value : value,
                      ),
                    )
                  }
                  placeholder={t('board.frameName', { number: index + 1 })}
                  maxLength={120}
                />
              </li>
            ))}
          </ol>
        </div>

        <ScopeChoice scope={scope} onChange={setScope} me={me} />

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('templates.saving') : t('boardTemplates.save')}
        </button>
        {error && <p className="form-error">{error}</p>}
      </form>
    </Dialog>
  )
}

/** Reichweite — dieselben drei Stufen wie bei den Frame-Vorlagen. */
function ScopeChoice({
  scope,
  onChange,
  me,
}: {
  scope: TemplateScope
  onChange: (scope: TemplateScope) => void
  me: MeDto
}): React.ReactElement {
  const t = useText()
  const scopes: TemplateScope[] = me.isPlatformAdmin
    ? ['personal', 'organisation', 'platform']
    : ['personal', 'organisation']

  return (
    <fieldset className="mode-choice">
      <legend className="sr-only">{t('templates.scope')}</legend>
      {scopes.map((candidate) => (
        <label key={candidate} className={scope === candidate ? 'is-selected' : undefined}>
          <input
            type="radio"
            name="board-template-scope"
            value={candidate}
            checked={scope === candidate}
            onChange={() => onChange(candidate)}
          />
          <span className="mode-body">
            <span className="mode-label">{t(TEMPLATE_SCOPE_LABEL[candidate])}</span>
            <span className="mode-description">{t(TEMPLATE_SCOPE_HINT[candidate])}</span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}


/**
 * Grundriss eines Gerüsts: die Frames als nummerierte Rechtecke.
 *
 * Die Nummer ist hier keine Zierde — sie ist die Vortragsreihenfolge, und die
 * ist bei einem Raster nicht aus der Lage abzulesen.
 */
export function BoardTemplateSketch({
  content,
}: {
  content: BoardTemplateContent
}): React.ReactElement {
  const bounds = boardTemplateBounds(content)
  // Die Schrift skaliert mit dem Gerüst, nicht mit der Kachel: Sonst wäre sie
  // bei sieben Frames winzig und bei einem riesig.
  const fontSize = Math.max(bounds.w, bounds.h) / 22

  return (
    <svg
      className="template-sketch board-template-sketch"
      viewBox={`${bounds.x} ${bounds.y} ${bounds.w || 1} ${bounds.h || 1}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {content.frames.map((frame, index) => (
        <BoardTemplateSketchFrame key={index} frame={frame} index={index} fontSize={fontSize} />
      ))}
    </svg>
  )
}

function BoardTemplateSketchFrame({
  frame,
  index,
  fontSize,
}: {
  frame: BoardTemplateFrame
  index: number
  fontSize: number
}): React.ReactElement {
  return (
    <>
      <rect
        x={frame.x}
        y={frame.y}
        width={frame.w}
        height={frame.h}
        rx={Math.min(frame.w, frame.h) * 0.04}
        fill={frame.style?.background || undefined}
      />
      <text
        x={frame.x + frame.w / 2}
        y={frame.y + frame.h / 2}
        fontSize={fontSize}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {index + 1}
      </text>
    </>
  )
}
